import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { adminService } from '../../services';
import { formatCurrency } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import NepaliDate from 'nepali-date-converter';
import { NepaliDatePicker } from 'nepali-datepicker-reactjs';
import 'nepali-datepicker-reactjs/dist/index.css';
import { API_BASE_URL } from '../../constants/config';
import api from '../../services/api';

const Donations = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language;
  const { isAdmin } = useAuth();
  const isAdminUser = isAdmin();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');

  // Applied filters (affect API calls)
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [targetTypeFilter, setTargetTypeFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Draft filters (modal only; do not auto-apply)
  const [showFilters, setShowFilters] = useState(false);
  const [draftStatusFilter, setDraftStatusFilter] = useState('ALL');
  const [draftTargetTypeFilter, setDraftTargetTypeFilter] = useState('ALL');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [dateMode, setDateMode] = useState('AD'); // AD | BS (draft)
  const [bsStartDate, setBsStartDate] = useState(''); // draft BS input
  const [bsEndDate, setBsEndDate] = useState(''); // draft BS input
  const [bsDateError, setBsDateError] = useState(''); // draft BS validation
  const [voucherPreview, setVoucherPreview] = useState(null);
  const [voucherPreviewBlobUrl, setVoucherPreviewBlobUrl] = useState(null);
  const [voucherPreviewLoading, setVoucherPreviewLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear message from history state so it doesn't reappear on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, targetTypeFilter, startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch stats, analytics, and donation list in parallel
      const params = {
        limit,
        offset: (currentPage - 1) * limit,
        sortBy: 'createdAt',
        order: 'DESC',
      };

      // Apply status filter only when explicitly selected
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }

      if (targetTypeFilter !== 'ALL') {
        params.targetType = targetTypeFilter;
      }

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }


      const [statsData, analyticsData, donationsData] = await Promise.all([
        adminService.getFinancialStats(),
        adminService.getDonationAnalytics(),
        adminService.getDonationsList(params, isAdminUser),
      ]);

      setStats(statsData?.data || statsData);
      setAnalytics(analyticsData?.data || analyticsData);

      const donationsList = donationsData?.data?.items || donationsData?.items || [];
      const total = donationsData?.data?.totalCount || donationsData?.totalCount || 0;

      setDonations(donationsList);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching donation data:', err);
      setError('Failed to load donation data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm(t('donations.confirmApprove') || 'Are you sure you want to approve this voucher?')) {
      return;
    }

    try {
      setLoading(true);
      await adminService.approveVoucher(id);
      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error approving voucher:', err);
      setError(err.response?.data?.message || t('donations.failedToApprove'));
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setDownloading(true);
    setError('');

    try {
      const params = {};

      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }

      if (targetTypeFilter !== 'ALL') {
        params.targetType = targetTypeFilter;
      }

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }

      const blob = await adminService.exportDonations(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donations_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting donations:', err);
      setError('Failed to export donations. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter(isAdminUser ? 'ALL' : 'SUCCESS');
    setTargetTypeFilter('ALL');
    setStartDate('');
    setEndDate('');
    setDraftStatusFilter(isAdminUser ? 'ALL' : 'SUCCESS');
    setDraftTargetTypeFilter('ALL');
    setDraftStartDate('');
    setDraftEndDate('');
    setDateMode('AD');
    setBsStartDate('');
    setBsEndDate('');
    setBsDateError('');
    setCurrentPage(1);
  };

  const toIsoDate = (jsDate) => {
    const y = jsDate.getFullYear();
    const m = String(jsDate.getMonth() + 1).padStart(2, '0');
    const d = String(jsDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getTodayBsIso = () => {
    const bs = new NepaliDate(new Date()).getBS();
    const y = bs?.year;
    const m = String((bs?.month ?? 0) + 1).padStart(2, '0');
    const d = String(bs?.date ?? 1).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    if (!showFilters) return;
    if (dateMode !== 'BS') return;
    // Default selection: today's BS date in the datepicker inputs.
    // We intentionally do NOT set startDate/endDate here to avoid applying a filter automatically.
    if (!bsStartDate) setBsStartDate(getTodayBsIso());
    if (!bsEndDate) setBsEndDate(getTodayBsIso());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters, dateMode]);

  const applyBsInput = (value, which) => {
    if (!value) {
      setBsDateError('');
      if (which === 'start') setDraftStartDate('');
      else setDraftEndDate('');
      return;
    }

    try {
      const nep = new NepaliDate(value); // supports YYYY-MM-DD etc.
      const js = nep.toJsDate();
      if (!(js instanceof Date) || Number.isNaN(js.getTime())) throw new Error('Invalid date');
      const iso = toIsoDate(js);
      setBsDateError('');
      if (which === 'start') setDraftStartDate(iso);
      else setDraftEndDate(iso);
    } catch (e) {
      setBsDateError(t('donations.invalidBsDate'));
    }
  };

  const openFilters = () => {
    setDraftStatusFilter(statusFilter);
    setDraftTargetTypeFilter(targetTypeFilter);
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setDateMode('AD');
    setBsDateError('');
    setShowFilters(true);
  };

  const applyFilters = () => {
    if (dateMode === 'BS' && bsDateError) return;
    setStatusFilter(draftStatusFilter);
    setTargetTypeFilter(draftTargetTypeFilter);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      SUCCESS: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getTargetTypeBadge = (type) => {
    const styles = {
      CANDIDATE: 'bg-blue-100 text-blue-800',
      PARTY: 'bg-purple-100 text-purple-800',
    };

    const label =
      type === 'PARTY'
        ? t('donations.organization') // गुरुकुल कोष
        : type === 'CANDIDATE'
          ? t('donations.gurukul') // एउटा गुरुकुल
          : type;

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / limit);

  const isPdfVoucher = (v) => {
    if (!v) return false;
    const s = String(v);
    return s.startsWith('data:application/pdf') || s.toLowerCase().includes('.pdf');
  };

  const getAbsoluteVoucherUrl = (value) => {
    if (!value) return null;
    const v = String(value);
    if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) return v;

    const originBase = API_BASE_URL.includes('/main/api/v1')
      ? API_BASE_URL.split('/main/api/v1')[0]
      : API_BASE_URL.replace(/\/+$/, '');

    if (v.startsWith('/')) return `${originBase}${v}`;
    return v;
  };

  const openVoucherPreview = async (raw) => {
    const resolved = getAbsoluteVoucherUrl(raw);
    setVoucherPreview(resolved);
    setVoucherPreviewBlobUrl(null);

    // For PDFs or data URLs, don't prefetch.
    if (!raw || String(raw).startsWith('data:') || isPdfVoucher(resolved)) return;

    // Best-effort prefetch via authenticated API (handles 401 on "public" endpoint).
    try {
      setVoucherPreviewLoading(true);
      const res = await api.get(raw, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      setVoucherPreviewBlobUrl(blobUrl);
    } catch (e) {
      // If this fails, we still keep the direct URL attempt.
    } finally {
      setVoucherPreviewLoading(false);
    }
  };

  const closeVoucherPreview = () => {
    setVoucherPreview(null);
    if (voucherPreviewBlobUrl) {
      try {
        URL.revokeObjectURL(voucherPreviewBlobUrl);
      } catch (e) {
        // noop
      }
    }
    setVoucherPreviewBlobUrl(null);
    setVoucherPreviewLoading(false);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      <PageHeader
        title={t('donations.publicDonations')}
        subtitle={t('site.description')}
      />

      <div className="max-w-7xl mx-auto px-4 pb-12">

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
            <h3 className="text-sm font-semibold">Voucher submitted</h3>
            <p className="text-xs mt-1">
              Your donation voucher has been submitted and is under review. You&apos;ll be notified once it&apos;s approved.
            </p>
            <p className="text-xs mt-1 text-green-700/80">
              {successMessage}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Filters Trigger and Export */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={openFilters}
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary-200 rounded-2xl text-base font-semibold text-primary-700 bg-white hover:bg-primary-50 transition-all duration-200 shadow-sm relative group"
            >
              <svg className="w-5 h-5 mr-2 text-primary-600 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {t('donations.filters')}
              {(statusFilter !== 'ALL' || targetTypeFilter !== 'ALL' || startDate || endDate) && (
                <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white ring-4 ring-primary-50">
                  {[statusFilter !== 'ALL', targetTypeFilter !== 'ALL', startDate, endDate].filter(Boolean).length}
                </span>
              )}
            </button>

            {(statusFilter !== 'ALL' || targetTypeFilter !== 'ALL' || startDate || endDate) && (
              <button
                onClick={handleResetFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-bold px-2 py-1 bg-primary-50 rounded-lg transition-colors"
              >
                {t('donations.clearFilters')}
              </button>
            )}
          </div>

          {isAdmin() && (
            <button
              onClick={handleExport}
              disabled={downloading}
              className="inline-flex items-center px-6 py-3 bg-amber-600 text-white rounded-2xl font-bold hover:bg-amber-700 transition-all disabled:opacity-50 shadow-lg shadow-amber-600/20"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {downloading ? t('donations.downloadingReport') : t('donations.exportReport')}
            </button>
          )}
        </div>

        {/* Filter Modal */}
        {showFilters && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div
              className="absolute inset-0 bg-primary-900/40 backdrop-blur-sm transition-opacity"
              onClick={() => setShowFilters(false)}
            ></div>

            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp">
              <div className="px-6 py-5 border-b border-primary-100 flex items-center justify-between bg-primary-50/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-xl text-primary-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{t('gurukul.advancedFilters')}</h3>
                </div>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-primary-100 rounded-full text-gray-400 hover:text-primary-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                    {t('donations.dateSystem')}
                  </label>
                  <div className="inline-flex rounded-2xl overflow-hidden border-2 border-primary-100 bg-primary-50/30">
                    <button
                      type="button"
                      onClick={() => {
                        setDateMode('AD');
                        setBsDateError('');
                      }}
                      className={`px-4 py-2 text-sm font-bold transition ${dateMode === 'AD'
                        ? 'bg-primary-600 text-white'
                        : 'text-primary-700 hover:bg-white'
                        }`}
                    >
                      {t('donations.ad')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDateMode('BS');
                        setBsDateError('');
                        const today = getTodayBsIso();
                        if (!bsStartDate) setBsStartDate(today);
                        if (!bsEndDate) setBsEndDate(today);
                      }}
                      className={`px-4 py-2 text-sm font-bold transition ${dateMode === 'BS'
                        ? 'bg-primary-600 text-white'
                        : 'text-primary-700 hover:bg-white'
                        }`}
                    >
                      {t('donations.bs')}
                    </button>
                  </div>
                  {dateMode === 'BS' && (
                    <p className="mt-2 text-xs text-gray-500">
                      {t('donations.bsFormatHint')}
                    </p>
                  )}
                  {dateMode === 'BS' && bsDateError && (
                    <p className="mt-2 text-xs text-red-600 font-semibold">
                      {bsDateError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('common.status')}</label>
                  <select
                    value={draftStatusFilter}
                    onChange={(e) => {
                      setDraftStatusFilter(e.target.value);
                    }}
                    className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                  >
                    <option value="ALL">{t('donations.allStatuses')}</option>
                    <option value="SUCCESS">{t('status.success')}</option>
                    <option value="PENDING">{t('status.pending')}</option>
                    <option value="FAILED">{t('status.failed')}</option>
                    <option value="CANCELLED">{t('status.cancelled')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('donations.targetType')}</label>
                  <select
                    value={draftTargetTypeFilter}
                    onChange={(e) => {
                      setDraftTargetTypeFilter(e.target.value);
                    }}
                    className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                  >
                    <option value="ALL">{t('donations.allTypes')}</option>
                    <option value="CANDIDATE">{t('donations.gurukul')}</option>
                    <option value="PARTY">{t('donations.organization')}</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('donations.startDate')}</label>
                    {dateMode === 'AD' ? (
                      <input
                        type="date"
                        value={draftStartDate}
                        onChange={(e) => {
                          setDraftStartDate(e.target.value);
                        }}
                        className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                      />
                    ) : (
                      <div className="w-full">
                        <NepaliDatePicker
                          value={bsStartDate}
                          onChange={(value) => {
                            setBsStartDate(value);
                            applyBsInput(value, 'start');
                          }}
                          inputClassName="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                          options={{
                            calenderLocale: lang === 'ne' ? 'ne' : 'en',
                            valueLocale: 'en',
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">{t('donations.endDate')}</label>
                    {dateMode === 'AD' ? (
                      <input
                        type="date"
                        value={draftEndDate}
                        onChange={(e) => {
                          setDraftEndDate(e.target.value);
                        }}
                        className="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                      />
                    ) : (
                      <div className="w-full">
                        <NepaliDatePicker
                          value={bsEndDate}
                          onChange={(value) => {
                            setBsEndDate(value);
                            applyBsInput(value, 'end');
                          }}
                          inputClassName="w-full px-4 py-3 bg-primary-50/50 border-2 border-primary-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-medium text-gray-700"
                          options={{
                            calenderLocale: lang === 'ne' ? 'ne' : 'en',
                            valueLocale: 'en',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-8 py-6 bg-primary-50/30 border-t border-primary-100 flex gap-4">
                <button
                  onClick={() => {
                    handleResetFilters();
                    setShowFilters(false);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-primary-200 text-primary-700 rounded-2xl font-bold hover:bg-white transition-all"
                >
                  {t('donations.clearFilters')}
                </button>
                <button
                  onClick={applyFilters}
                  disabled={dateMode === 'BS' && !!bsDateError}
                  className={`flex-1 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-primary-600/20 transition-all ${dateMode === 'BS' && !!bsDateError
                    ? 'bg-gray-400 text-white cursor-not-allowed shadow'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                >
                  {t('gurukul.applyFilters')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Donations Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.date')}
                  </th>
                  {isAdmin() && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.transactionId')}
                  </th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.donor')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.target')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('donations.targetType')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  {isAdmin() && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Voucher
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={isAdmin() ? "10" : "6"} className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : donations.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin() ? "10" : "6"} className="px-6 py-12 text-center text-gray-500">
                      {t('donations.noDonations')}
                    </td>
                  </tr>
                ) : (
                  donations.map((donation) => (
                    <tr key={donation.id} className="hover:bg-primary-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(donation.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(donation.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      {isAdmin() && <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {donation.receiptNumber || "N/A"}
                        </div>
                      </td>}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {donation.isAnonymous ? t('donations.anonymous') : (donation.donor?.fullName || 'Anonymous')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {donation.candidate?.name || donation.party?.name || 'Gurukul Fund'}
                        </div>
                        {donation.candidate?.constituency && (
                          <div className="text-xs text-gray-500">
                            {donation.candidate.constituency}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTargetTypeBadge(donation.targetType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(donation.amount, lang)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(donation.status)}
                      </td>
                      {isAdmin() && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {donation.voucherImageUrl || donation.voucherImage ? (
                              <button
                                type="button"
                                onClick={() => openVoucherPreview(donation.voucherImageUrl || donation.voucherImage)}
                                className="text-primary-600 hover:text-primary-800 text-sm font-medium underline"
                              >
                                {t('donations.viewVoucher')}
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>

                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('pagination.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('pagination.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('pagination.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('pagination.of')}{' '}
                    <span className="font-medium">{totalCount}</span> {t('pagination.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('pagination.previous')}
                    </button>
                    {(() => {
                      const maxButtons = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                      let endPage = Math.min(totalPages, startPage + maxButtons - 1);

                      if (endPage - startPage + 1 < maxButtons) {
                        startPage = Math.max(1, endPage - maxButtons + 1);
                      }

                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }

                      return pages.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('pagination.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {voucherPreview && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4 overflow-y-auto">
          <button
            type="button"
            className="absolute top-4 right-4 z-[60] text-white hover:text-gray-200"
            onClick={closeVoucherPreview}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {isPdfVoucher(voucherPreview) ? (
            <div className="my-auto w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
              <a
                href={voucherPreview}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-primary-700 font-semibold bg-primary-50 px-3 py-2 rounded-lg border border-primary-200 hover:bg-primary-100 transition"
              >
                View PDF
              </a>
            </div>
          ) : (
            <div className="relative my-auto inline-block max-h-[min(92vh,92dvh)] max-w-[min(100vw-2rem,72rem)] rounded-xl shadow-2xl overflow-hidden align-middle">
              {voucherPreviewLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent drop-shadow-md" />
                </div>
              )}
              <img
                src={voucherPreviewBlobUrl || voucherPreview}
                alt="Voucher"
                className="block max-h-[min(85vh,85dvh)] max-w-full w-auto h-auto object-contain"
                  onError={async () => {
                    if (voucherPreviewBlobUrl) return;
                    try {
                      setVoucherPreviewLoading(true);
                      // Try fetching using the relative path (auth-aware client)
                      const raw = donations.find((d) => getAbsoluteVoucherUrl(d.voucherImageUrl || d.voucherImage) === voucherPreview)?.voucherImageUrl
                        || donations.find((d) => getAbsoluteVoucherUrl(d.voucherImageUrl || d.voucherImage) === voucherPreview)?.voucherImage;
                      if (!raw || String(raw).startsWith('data:')) return;
                      const res = await api.get(raw, { responseType: 'blob' });
                      const blobUrl = URL.createObjectURL(res.data);
                      setVoucherPreviewBlobUrl(blobUrl);
                    } catch (e) {
                      // Give up; keep broken image indicator from browser
                    } finally {
                      setVoucherPreviewLoading(false);
                    }
                  }}
                />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Donations;
